import React, { useState } from 'react';
import { Button, Radio, Tabs } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { nextLetterId, optionLetter } from '../cardTypes';
import { AddButton, Field, isBlank, ItemActions, plainText, RichTextArea, Section } from '../fields';
import { asArray, cloneJson, insertAt, moveItem, removeAt, replaceAt } from '../listOps';
import { Bubble, BubbleList, ChatBubble } from './BubbleList';
import { CardEditorProps } from './types';

type Quality = 'good' | 'mid' | 'bad';

interface ChatChoice {
  id: string;
  text: string;
  quality: Quality;
  herReply: Bubble[];
  verdict: string;
  [key: string]: unknown;
}

const QUALITY_OPTIONS = [
  { label: 'Good', value: 'good' },
  { label: 'Mid', value: 'mid' },
  { label: 'Bad', value: 'bad' },
];

/** The verdict heading the app shows for each quality (alpha-web-fe TestChat). */
const VERDICT_HEADING: Record<Quality, string> = {
  good: 'Отличный ответ',
  mid: 'Неплохо, но можно лучше',
  bad: 'Слабый ответ',
};

const qualityOf = (choice: ChatChoice): Quality =>
  choice.quality === 'good' || choice.quality === 'mid' ? choice.quality : 'bad';

const truncate = (text: string, max: number) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

const ChatSimulatorEditor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const opening = asArray<Bubble>(card.initialMessages);
  const choices = asArray<ChatChoice>(card.choices);
  const [active, setActive] = useState(0);
  const selected = Math.min(active, choices.length - 1);

  const setChoices = (next: ChatChoice[], activate?: number) => {
    onChange({ ...card, choices: next });
    if (activate !== undefined) setActive(activate);
  };
  const patchChoice = (index: number, patch: Partial<ChatChoice>) =>
    setChoices(replaceAt(choices, index, { ...choices[index], ...patch }));
  const freshId = () => nextLetterId(choices.map((c) => c.id));

  const addChoice = () =>
    setChoices(
      [...choices, { id: freshId(), text: '', quality: 'mid', herReply: [{ text: '', delay: 1000 }], verdict: '' }],
      choices.length,
    );

  const renderBranch = (choice: ChatChoice, index: number) => {
    const quality = qualityOf(choice);
    return (
      <div className="lbe-fields">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span className="lbe-label">Quality</span>
            <Radio.Group
              size="small"
              optionType="button"
              buttonStyle="solid"
              value={quality}
              options={QUALITY_OPTIONS}
              onChange={(e) => patchChoice(index, { quality: e.target.value })}
            />
          </span>
          <ItemActions
            index={index}
            count={choices.length}
            onMove={(to) => setChoices(moveItem(choices, index, to), to)}
            onDuplicate={() => setChoices(insertAt(choices, index + 1, { ...cloneJson(choice), id: freshId() }), index + 1)}
            onRemove={() => setChoices(removeAt(choices, index), Math.max(0, index - 1))}
            confirmRemove={`Delete choice ${optionLetter(index)}?`}
          />
        </div>

        <div className="lbe-chat">
          {opening.length > 0 && (
            // Read-only replay of the opening, so the branch reads as one conversation.
            <div style={{ opacity: 0.5, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {opening.map((m, i) => (
                <ChatBubble key={i} from={m.from === 'you' ? 'you' : 'her'}>
                  <span style={{ fontSize: 12 }}>{plainText(m.text) || '…'}</span>
                </ChatBubble>
              ))}
            </div>
          )}
          <ChatBubble from="you">
            <RichTextArea
              minRows={1}
              value={choice.text}
              status={isBlank(choice.text) ? 'warning' : undefined}
              placeholder="The reply the user picks"
              onChange={(text) => patchChoice(index, { text })}
            />
          </ChatBubble>
          <BubbleList
            items={asArray<Bubble>(choice.herReply)}
            speakers="her"
            delay="required"
            onChange={(herReply) => patchChoice(index, { herReply })}
          />
        </div>

        <div className={`lbe-verdict quality-${quality}`}>
          <Field label={`Verdict — shown under “${VERDICT_HEADING[quality]}”`} required>
            <RichTextArea
              value={choice.verdict}
              status={isBlank(choice.verdict) ? 'warning' : undefined}
              onChange={(verdict) => patchChoice(index, { verdict })}
            />
          </Field>
        </div>
      </div>
    );
  };

  return (
    <>
      <Section title="Opening messages" help="Played in order when the user starts the chat. Shared by every choice.">
        <div className="lbe-chat">
          <BubbleList items={opening} speakers="pick" onChange={(initialMessages) => onChange({ ...card, initialMessages })} />
        </div>
      </Section>

      <Section
        title="Reply choices"
        help="The user picks one reply, then sees her reaction and a verdict. Pick a choice to edit its branch."
      >
        {choices.length === 0 ? (
          <AddButton onClick={addChoice}>Add choice</AddButton>
        ) : (
          <Tabs
            type="card"
            size="small"
            activeKey={String(selected)}
            onChange={(key) => setActive(Number(key))}
            tabBarExtraContent={
              <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addChoice}>Add choice</Button>
            }
            items={choices.map((choice, i) => ({
              key: String(i),
              label: (
                <span>
                  <span className={`lbe-quality-dot quality-dot-${qualityOf(choice)}`} />
                  {optionLetter(i)} · {truncate(plainText(choice.text), 28) || 'empty'}
                </span>
              ),
              children: renderBranch(choice, i),
            }))}
          />
        )}
      </Section>
    </>
  );
};

export default ChatSimulatorEditor;
